import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Info as InfoIcon,
  ShowChart as ShowChartIcon,
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const RETENTION_COLORS = [
  '#d32f2f', // < 20% - red
  '#f57c00', // 20-40% - orange
  '#fbc02d', // 40-60% - yellow
  '#7cb342', // 60-80% - light green
  '#2e7d32', // > 80% - dark green
];

function getRetentionColor(rate: number): string {
  if (rate >= 80) return RETENTION_COLORS[4];
  if (rate >= 60) return RETENTION_COLORS[3];
  if (rate >= 40) return RETENTION_COLORS[2];
  if (rate >= 20) return RETENTION_COLORS[1];
  return RETENTION_COLORS[0];
}

export default function RetentionAnalytics() {
  const [periodType, setPeriodType] = useState<'daily' | 'weekly'>('weekly');
  const [cohortsCount, setCohortsCount] = useState(26);
  const [retentionPeriods, setRetentionPeriods] = useState(26);
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [viewMode, setViewMode] = useState<'heatmap' | 'chart'>('heatmap');

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'retentionAnalytics',
      periodType,
      cohortsCount,
      retentionPeriods,
      subscriptionType,
      source,
      includeBlocked,
    ],
    queryFn: () =>
      statsApi.getRetentionAnalytics(
        periodType,
        cohortsCount,
        retentionPeriods,
        subscriptionType || undefined,
        source || undefined,
        includeBlocked
      ),
    refetchInterval: 300000, // Обновление каждые 5 минут
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Ошибка загрузки Retention Rate: {(error as Error).message}
        </Alert>
      </Box>
    );
  }

  // Подготовка данных для графиков
  const chartData = data && data.cohorts.length > 0
    ? data.cohorts.slice(0, 5).map((cohort) => {
        const cohortData: any = {
          name: cohort.cohort,
        };
        cohort.retention_by_period.forEach((period) => {
          cohortData[`period_${period.period_number}`] = period.retention_rate;
        });
        return cohortData;
      })
    : [];

  // Максимальный номер периода для отображения
  const maxPeriod = data && data.cohorts.length > 0
    ? Math.max(...data.cohorts.flatMap((c) => c.retention_by_period.map((p) => p.period_number)))
    : 12;

  return (
    <Box>
      {/* Информация о расчётах */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Как считается Retention Rate:</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Когорта</strong> - группа пользователей, зарегистрировавшихся в один период (неделю/день)<br />
          • <strong>Активность</strong> - успешная транскрипция (processing_sessions.final_status = 'success')<br />
          • <strong>Период 0</strong> - период регистрации (базовый, 100%)<br />
          • <strong>Период N</strong> - % пользователей когорты, которые были активны в период N после регистрации<br />
          • <strong>Пример</strong>: Когорта "2024-W01" (10 пользователей) → Период 1: 7 активных = 70% retention<br />
          • <strong>Типы подписок</strong>: weekly, monthly, semiannual, yearly<br />
          • <strong>Источники</strong>: анализ по полю users.source (UTM-метки)
        </Typography>
      </Alert>

      {/* Фильтры */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип периода</InputLabel>
            <Select
              value={periodType}
              label="Тип периода"
              onChange={(e) => setPeriodType(e.target.value as 'daily' | 'weekly')}
            >
              <MenuItem value="weekly">По неделям</MenuItem>
              <MenuItem value="daily">По дням</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Когорт</InputLabel>
            <Select
              value={cohortsCount}
              label="Когорт"
              onChange={(e) => setCohortsCount(Number(e.target.value))}
            >
              <MenuItem value={4}>4 {periodType === 'weekly' ? 'недели' : 'дня'}</MenuItem>
              <MenuItem value={8}>8 {periodType === 'weekly' ? 'недель' : 'дней'}</MenuItem>
              <MenuItem value={12}>12 {periodType === 'weekly' ? 'недель' : 'дней'}</MenuItem>
              <MenuItem value={26}>26 {periodType === 'weekly' ? 'недель (~6 мес)' : 'дней'}</MenuItem>
              <MenuItem value={52}>52 {periodType === 'weekly' ? 'недели (год)' : 'дня'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Периоды</InputLabel>
            <Select
              value={retentionPeriods}
              label="Периоды"
              onChange={(e) => setRetentionPeriods(Number(e.target.value))}
            >
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={8}>8</MenuItem>
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={26}>26</MenuItem>
              <MenuItem value={52}>52</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип подписки</InputLabel>
            <Select
              value={subscriptionType}
              label="Тип подписки"
              onChange={(e) => setSubscriptionType(e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="weekly">Недельная</MenuItem>
              <MenuItem value="monthly">Месячная</MenuItem>
              <MenuItem value="semiannual">Полугодовая</MenuItem>
              <MenuItem value="yearly">Годовая</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Источник</InputLabel>
            <Select
              value={source}
              label="Источник"
              onChange={(e) => setSource(e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="organic">Органика</MenuItem>
              <MenuItem value="google_ads">Google Ads</MenuItem>
              <MenuItem value="facebook_ads">Facebook Ads</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="telegram">Telegram</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeBlocked}
                onChange={(e) => setIncludeBlocked(e.target.checked)}
              />
            }
            label="Заблокированные"
          />
        </Grid>
      </Grid>

      {/* Переключатель режима отображения */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Retention Rate: {data?.total_cohorts || 0} когорт
        </Typography>
        {data && data.cohorts.length > 0 && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) setViewMode(newMode);
            }}
            size="small"
          >
            <ToggleButton value="heatmap">
              <TableChartIcon sx={{ mr: 1 }} />
              Тепловая карта
            </ToggleButton>
            <ToggleButton value="chart">
              <ShowChartIcon sx={{ mr: 1 }} />
              Графики
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Сообщение если нет данных */}
      {(!data || data.cohorts.length === 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Нет данных для отображения</strong>
          </Typography>
          <Typography variant="body2" component="div">
            Возможные причины:<br />
            • Нет пользователей с активностью (успешные транскрипции) за выбранный период<br />
            • Выбран слишком короткий период или неправильные фильтры<br />
            • Попробуйте увеличить количество когорт или изменить тип периода<br />
            • Убедитесь, что в базе есть данные в таблицах users и processing_sessions
          </Typography>
        </Alert>
      )}

      {/* Тепловая карта или графики */}
      {data && data.cohorts.length > 0 && (
        <>
          {viewMode === 'heatmap' ? (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Тепловая карта Retention Rate
                  </Typography>
                  <Tooltip
                    title="Цвет показывает % удержания: зелёный (>80%), жёлтый (40-80%), красный (<40%)"
                    arrow
                  >
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Когорта</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }} align="right">
                          Всего
                        </TableCell>
                        {Array.from({ length: Math.min(maxPeriod + 1, 13) }, (_, i) => (
                          <TableCell key={i} sx={{ fontWeight: 'bold', minWidth: 60 }} align="center">
                            {periodType === 'weekly' ? `W${i}` : `D${i}`}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.cohorts.map((cohort) => {
                        const retentionMap = new Map(
                          cohort.retention_by_period.map((p) => [p.period_number, p.retention_rate])
                        );

                        return (
                          <TableRow key={cohort.cohort} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{cohort.cohort}</TableCell>
                            <TableCell align="right">{cohort.total_users}</TableCell>
                            {Array.from({ length: Math.min(maxPeriod + 1, 13) }, (_, i) => {
                              const rate = retentionMap.get(i);
                              return (
                                <TableCell
                                  key={i}
                                  align="center"
                                  sx={{
                                    backgroundColor: rate ? getRetentionColor(rate) : '#f5f5f5',
                                    color: rate && rate > 50 ? '#fff' : '#000',
                                    fontWeight: 600,
                                  }}
                                >
                                  {rate !== undefined ? `${rate.toFixed(1)}%` : '—'}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Легенда */}
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['< 20%', '20-40%', '40-60%', '60-80%', '> 80%'].map((label, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: RETENTION_COLORS[idx],
                          borderRadius: 1,
                        }}
                      />
                      <Typography variant="caption">{label}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Графики Retention Rate (5 последних когорт)
                  </Typography>
                  <Tooltip
                    title="Показаны 5 самых свежих когорт для удобства сравнения"
                    arrow
                  >
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                      label={{ value: 'Retention Rate (%)', angle: -90, position: 'insideLeft' }}
                    />
                    <ChartTooltip />
                    <Legend />
                    {data.cohorts.slice(0, 5).map((cohort, idx) => (
                      <Line
                        key={cohort.cohort}
                        type="monotone"
                        dataKey={cohort.cohort}
                        stroke={RETENTION_COLORS[idx % RETENTION_COLORS.length]}
                        strokeWidth={2}
                        name={`${cohort.cohort} (${cohort.total_users} польз.)`}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Детальная таблица */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Детальные данные
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Когорта</TableCell>
                      <TableCell align="right">Дата</TableCell>
                      <TableCell align="right">Всего пользователей</TableCell>
                      <TableCell align="right">Период 1 (retention)</TableCell>
                      <TableCell align="right">Период 4 (retention)</TableCell>
                      <TableCell align="right">Период 12 (retention)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.cohorts.map((cohort) => {
                      const p1 = cohort.retention_by_period.find((p) => p.period_number === 1);
                      const p4 = cohort.retention_by_period.find((p) => p.period_number === 4);
                      const p12 = cohort.retention_by_period.find((p) => p.period_number === 12);

                      return (
                        <TableRow key={cohort.cohort} hover>
                          <TableCell>{cohort.cohort}</TableCell>
                          <TableCell align="right">{cohort.cohort_start_date}</TableCell>
                          <TableCell align="right">{cohort.total_users}</TableCell>
                          <TableCell align="right">
                            {p1 ? `${p1.retention_rate.toFixed(1)}%` : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {p4 ? `${p4.retention_rate.toFixed(1)}%` : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {p12 ? `${p12.retention_rate.toFixed(1)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
